import { ChangeDetectionStrategy, ChangeDetectorRef,
  Component, OnInit, ViewChild, ViewContainerRef, OnDestroy, NgZone } from '@angular/core';
import { SharedChangesService, ProgressChangesService } from '@shared/services/index';
import { ShopboxTabComponent } from '@shared/components/component-schema/shopbox-tab-component.class';
import { Graph } from '@shared/models/graph/graph.class';
import { StoreBranches } from '@shared/models/store/store-branches.class';
import { TranslateService } from '@shared/services/translate/translate.service';
import { DialogsService } from '@shared/services/dialogs/dialogs.service';
import { Utilities } from '@shared/models/utilities/utilities.class';
import { combineLatest, of, ReplaySubject, catchError, distinctUntilChanged, take } from 'rxjs';
import { ShopboxErrors } from '@shared/models/shopbox-errors/shopbox-errors.class';
import { CacheMapService } from '@shared/services/cache';
import { APIFacadeService, API_TYPES,
  ERROR_TYPES, NextInterface, ErrorInterface, CompleteInterface } from '@shared/services/facades/api-facade.service';
import { HtmlToPdf } from '@shared/models/html-to-pdf/html-to-pdf.class';
import { MatTabChangeEvent, MatTabGroup, MatTab } from '@angular/material/tabs';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { OverviewToolbarComponent } from '@modules/dashboard/components';
import { GraphNotVat } from '@shared/models/graph/graph-not-vat.class';
import { GraphDataFilters, GraphDataMode } from '@shared/models/graph/graph-filters.class';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { OverviewToolbarComponent as OverviewToolbarComponent_1 } from '../../components/overview-toolbar/overview-toolbar.component';
import { MatCard, MatCardTitle, MatCardContent, MatCardHeader } from '@angular/material/card';
import { NgClass, UpperCasePipe } from '@angular/common';
import { MatDivider } from '@angular/material/list';
import { UIChartComponent } from '../../../shopbox-chart/shopbox-chart.module';
import { TranslatePipe } from '../../../../shared/pipes/translate.pipe';

dayjs.extend(isoWeek);

@Component({
    selector: 'shopbox-overview-tab',
    templateUrl: './overview-tab.component.html',
    styleUrls: ['./overview-tab.component.scss'],
    providers: [APIFacadeService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [OverviewToolbarComponent_1, MatCard, MatCardTitle, MatCardContent, MatDivider, MatTabGroup, MatTab, UIChartComponent, MatCardHeader, TranslatePipe, UpperCasePipe]
})
export class OverviewTabComponent extends ShopboxTabComponent implements OnInit, OnDestroy {
  @ViewChild('spanVis', { static: false}) spanV;
  @ViewChild('pChart', { static: false}) pChart;
  @ViewChild('toolbarView', { static: false }) toolbarView: OverviewToolbarComponent;

  selectedGroup = 0;
  currentStore: any = 'null';
  currentDate = '0';
  selectedDateRange: string = undefined;
  dateRange = undefined;
  dashboardData: Graph = <Graph>{};
  dashboardDataNotVat: GraphNotVat = <GraphNotVat>{};
  dashboardDataView: Graph | GraphNotVat = <Graph>{};
  storeList = [];
  clientList = [];

  lineData = [{ x: 1, y: 5 }, { x: 20, y: 20 }, { x: 40, y: 10 }, { x: 60, y: 40 }, { x: 80, y: 5 }, { x: 100, y: 60 }];
  widthSV: any = '100%';
  heightSV: any = 300;
  topStaff = [];
  topItems = [];
  topTags = [];

  dataSet: number[] = new Array<number>(24);
  dataGraph = {} as any;
  options = {} as any;

  dshAnalytics = true;
  dshGoals = true;

  firstDay: number;
  secondDay: number;
  graphMode = 3; // @default mode = 3
  graphFilterMode: GraphDataMode = GraphDataMode.Hourly;
  graphLabel: string = 'BY_HOUR';

  shownDate = '';
  touch = new Utilities().isMobileDevice();
  dateCustomDate = null;
  datePicker: string;
  isDownloadable = true;
  isUpdateable = true;
  isFilterable = true;
  activeComponentSubject: ReplaySubject<any> = new ReplaySubject();
  chartView = false;

  footerCards = ['TOP_CATEGORY_SALES', 'TOP_ITEMS_SALES', 'TOP_STAFF'];
  footerDataSources = [this.topTags, this.topItems, this.topStaff];

  hasChildren: boolean = false;
  checkChildren: boolean = false;
  currentChildrenClientId: any;

  startDateGraph: UntypedFormControl;
  endDateGraph: UntypedFormControl;
  clientChildrenGraph: UntypedFormControl;
  storeGraph: UntypedFormControl;
  typeGraph: UntypedFormControl;

  graphGroup: UntypedFormGroup;

  dateStart: any;
  dateEnd: any;
  previousClient: number;
  storeSelectorOn: boolean = true;

  currentClient: any;

  isCompareRevenueOn: boolean = true;
  compareRevenueToDates: string = '';
  vatOnValue: boolean = true;
  isAllClients: boolean = false;

  constructor(
    public _translate: TranslateService,
    private dialogSrv: DialogsService,
    private viewRef: ViewContainerRef,
    private apiSrv: APIFacadeService,
    private _changeDetectRef: ChangeDetectorRef,
    private changeSimple: SharedChangesService,
    private cacheSrv: CacheMapService,
    private zone: NgZone,
    // private _ga: GaService,
    public progressChange: ProgressChangesService
  ) {
    super(_translate);
    this.subscribeFunctions();
    this.graphGroup = this.initForm();
  }

  ngOnInit() {
    if (this.viewPermission) {
      this.currencyWatch();
      this.refreshTrans();
      this.currentCurrency = this.apiSrv.shopboxApiService.graphService.getCurrency() || 'DKK';
      this.currentClient = this.apiSrv.shopboxApiService.graphService.getCurrentClient();
      if (this.currentClient) {
        this.hasChildren = this.apiSrv.shopboxApiService.graphService.getCurrentClient()['hasChildren'];
        this.init();
        this.getClientsOrBranchesBasedOnHasChildren();
      }
    }
  }

  initForm(): UntypedFormGroup {
    this.startDateGraph = new UntypedFormControl();
    this.endDateGraph   = new UntypedFormControl();
    this.storeGraph     = new UntypedFormControl();
    this.clientChildrenGraph = new UntypedFormControl();
    this.typeGraph = new UntypedFormControl();
    const group = new UntypedFormGroup({
      start: this.startDateGraph,
      end: this.endDateGraph,
      type: this.typeGraph,
      store: this.storeGraph,
      child: this.clientChildrenGraph,
    });

    const dataGraphChangedSub = group.valueChanges.pipe(
      distinctUntilChanged()
    ).subscribe((res) => {
      if (res.store) {
        this.resetCaches();
        this.getData();
      }
    });

    this.apiSrv.subscriptionList.add(dataGraphChangedSub);

    return group;
  }

  getClientsOrBranchesBasedOnHasChildren() {
    if (this.hasChildren) {
      this.storeSelectorOn = false;
      this.getChildrenClients();
    } else {
      this.getBranches();
    }
  }

  subscribeFunctions() {
    this.apiSrv.subscribeToApi(this._changeDetectRef);
    this.apiSrv.nextEmit.subscribe((res: NextInterface) => {
      this.nextFn(res.res, res.apiType);
    });
    this.apiSrv.errorEmit.subscribe((res: ErrorInterface) => {
      this.errorFn(res.error, res.errorType);
    });
    this.apiSrv.completeEmit.subscribe((res: CompleteInterface) => {
      this.completeFn(res.apiType);
    });

  }

  refreshTrans() {
    const translateChanges = this._translate.onLangChanged.subscribe(() => {
      this._changeDetectRef.markForCheck();
    });
    this.apiSrv.subscriptionList.add(translateChanges);
  }

  currencyWatch() {
    const currencyChanges = this.changeSimple.currentCurrencyChange.subscribe(
      (res) => {
        if (res) {
          this.currentCurrency = res;
          this._changeDetectRef.markForCheck();
        }
      }
    );
    this.apiSrv.subscriptionList.add(currencyChanges);
  }

  // clientWatch() {
  //   const clientChanges = this.changeSimple.currentClientChange.subscribe(
  //     (res) => {
  //       this.ngOnInit();
  //     }
  //   );
  //   this.apiSrv.subscriptionList.add(clientChanges);
  // }

  nextFn(res: any, API: API_TYPES) {
    if (API === API_TYPES.GET_CHILDREN_CLIENTS) {
      if (res && res.data) {
        const source = [];
        source.push(this.currentClient);
        res.data.forEach(
          (element: any) => {
            source.push(element);
          }
        );
        this.clientList = source;
      }
    }
    if (API === API_TYPES.GET_BRANCHES) {
      if (res && res.data) {
        const source = [];
        res.data.forEach(
          (element: any) => {
            const store = new StoreBranches(element);
            source.push(store);
          }
        );
        this.storeList = source;
      }
    } else if (API === API_TYPES.GET_GRAPH) {
      const data1 = res[0];
      // const data2 = res[1];
      // const data3 = res[2];
      // const data4 = res[3];
      const data5 = res[1];

      if (data1) {
        this.dashboardData = new Graph(data1, this.currentCurrency);
        this.dashboardDataNotVat = new GraphNotVat(data1, this.currentCurrency);
        this.graphFilterMode = this.dashboardData.graphMode;
        this.changeChartType(this.graphFilterMode);
        this.changeVatValues(this.vatOnValue);
      }
      // if (data2) {
      //   if (data2 && data2.collection) {
      //     this.topStaff = this.getTopsOnVat(data2.collection, this.vatOnValue);
      //   }
      // }
      // if (data3) {
      //   if (data3 && data3.collection) {
      //     this.topItems = this.getTopsOnVat(data3.collection, this.vatOnValue);
      //   }
      // }
      // if (data4) {
      //   if (data4.collection) {

      //     this.topTags = this.getTopsOnVat(data4.collection, this.vatOnValue);
      //   }
      // }

      if(data5) {
        if(data5 && data5.staff) {
          this.topStaff = this.getTopsOnVat(data5.staff, this.vatOnValue);
        }
        if(data5 && data5.products) {
          this.topItems = this.getTopsOnVat(data5.products, this.vatOnValue);
        }
        if(data5 && data5.tags) {
          this.topTags = this.getTopsOnVat(data5.tags, this.vatOnValue);
        }
      }
    }
    this.footerDataSources = [this.topTags, this.topItems, this.topStaff];
  }

  errorFn(error: any, ERROR: ERROR_TYPES) {
    if (ERROR === ERROR_TYPES.NORMAL) {
      this.dialogSrv.toast(error, this.viewRef, 3200, true);
    } else if (ERROR === ERROR_TYPES.FORM) {
      const handle = new ShopboxErrors(error);
      if (handle.generalMessage) {
        this.dialogSrv.toast(handle.generalMessage, this.viewRef, 3200, true);
      } else {
        if (handle.allMessages[0]) {
          this.dialogSrv.toast(handle.allMessages[0], this.viewRef, 3200, true);
        }
      }
    }
    this.changeProgress(false, this._changeDetectRef);
  }

  completeFn(API: API_TYPES) {
    if (API === API_TYPES.GET_CHILDREN_CLIENTS) {
      this.currentChildrenClientId = this.clientList.length === 1 ? this.clientList[0].uid : 'null';
      this.isAllClients = this.clientList.length > 1;
      this.changeProgress(false, this._changeDetectRef);
      this.getBranches();
    }
    if (API === API_TYPES.GET_BRANCHES) {
      this.currentStore = this.storeList.length === 1 ? this.storeList[0].uid : 'null';
      this.changeProgress(false, this._changeDetectRef);
      this.getData();
    } else if (API === API_TYPES.GET_GRAPH) {
      this.setChartValues();
      this.changeProgress(false, this._changeDetectRef);
    }
   }

   setChartValues() {
     setTimeout(() => {
      this.widthSV = this.spanV.nativeElement.clientWidth;
      this.heightSV = this.spanV.nativeElement.clientHeight - 50;
      setTimeout(() => {
        this.updateTheChartView();
      }, 1000);
     }, 1000);
   }

  downloadPdf() {
    const htmlToPdf = new HtmlToPdf();
    htmlToPdf.doneEvent.subscribe((res) => {
      this.changeProgress(false, this._changeDetectRef);
    });

    this.zone.runOutsideAngular(() => {
      this.changeProgress(true, this._changeDetectRef);
      htmlToPdf.createCanvasFromId('dashboard', 'dashboard-overview');
      // this._ga.sendEvent('Dashboard Overview', 'Download', 'Download Dashboard as PDF');
    });
  }

  init() {
    if (this.toolbarView) {
      this.toolbarView.reset();
    }
    this.setDataGraph();
    this.storeGraph.setValue(null, {emitEvent: false });
    this.setChartDate();
    this.resetCaches();
  }

  refreshChart() {
    this.resetCaches();
    this.getData();
  }

  getSelectedDates(value: string) {
    this.shownDate = value;
  }

  setDataGraphView() {
    this.dataGraph = {
      labels: [],
      datasets: [
        {
          label: '',
          data: [],
          fill: true,
          lineTension: 0.1,
          borderColor: 'rgba(76,175,80,1)',
          backgroundColor: 'rgba(76,175,80,0.6)',
          borderWidth: 1
        }
      ]
    };
  }

  setGraphOptions() {
    const currentCurrency = this.currentCurrency;

    this.options = {
      animation: {
        duration: 500
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          enabled: true,
          mode: 'nearest',
          intersect: true,
          titleColor: 'rgba(0,0,0,0.7)',
          bodyColor: 'rgba(0,0,0,0.7)',
          backgroundColor: 'rgba(255,255,255,1)',
          borderColor: 'rgba(0,0,0,0.1)',
          borderWidth: 1,
          cornerRadius: 0,
          caretSize: 0,
          callbacks: {
            label(context: any) {
              return new Utilities().formatMoney(context.parsed.y, currentCurrency) + ' ' + currentCurrency;
            },
          },
        },
      },
      scales: {
        x: {
          display: true,
          grid: {
            display: false
          }
        },
        y: {
          display: true,
          beginAtZero: true,
          grid: {
            display: true
          }
        }
      },
      responsive: true,
      maintainAspectRatio: true
    };
  }

  setDataGraph() {
    this.setDataGraphView();
    this.setGraphOptions();
  }

  getThisWeek() {
    let startOfWeek: any = dayjs().startOf('isoWeek').toDate();
    let endOfWeek: any = dayjs().endOf('isoWeek').toDate();
    startOfWeek = this.formatDate(startOfWeek, null, false, true);
    endOfWeek = this.formatDate(endOfWeek, null, false, true);
    return [startOfWeek, endOfWeek];
  }

  getThisMonth() {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    let firstDay: any = new Date(y, m, 1);
    let lastDay: any = new Date(y, m + 1, 0);
    firstDay = this.formatDate(firstDay, null, false, true);
    lastDay = this.formatDate(lastDay, null, false, true);
    return [firstDay, lastDay];
  }

  getToday(): any[] {
    const start: any = new Date();
    const startDate: any = this.formatDate(start, null, false, true);
    const endDate: any = this.formatDate(start, null, false, true);
    return [startDate, endDate];
  }

  formatDate(isoDate: string, divider: string, exist: boolean, reflect?: boolean): string {
    if (!reflect) {
      reflect = false;
    }
    return new Utilities().formatDateReflact(isoDate, divider, exist, reflect);
  }

  revertDateFormat(dateFormated, divider) {
    let result = '';
    if (dateFormated) {
      const arrayForm = dateFormated.split('');
      const year = arrayForm[0] + arrayForm[1] + arrayForm[2] + arrayForm[3];
      const month = arrayForm[4] + arrayForm[5];
      const day = arrayForm[6] + (arrayForm[7] ? arrayForm[7] : '');
      result = day + divider + month + divider + year;
    }

    return result;
  }

  storeAndDateChanged(changes: any) {
    const dates = changes.datePicker;
    const store = changes.store;
    const client = changes.client;
    this.isAllClients = client === 'null';
    this.selectCustomDate(dates);
    if (dates) {
      this.typeGraph.setValue(dates.type, {emitEvent: false});
      if (client !== 'null') {
        this.storeSelectorOn = true;
        this.clientChildrenGraph.setValue(client, { emitEvent: false });
      } else {

        if (this.hasChildren) {
          this.storeSelectorOn = false;
          this.clientChildrenGraph.setValue(null, { emitEvent: false });
        }
      }
      if (this.previousClient !== client) {
        this.previousClient = client;
        this.toolbarView.store.setValue(null, { emitEvent: false});
        this.storeGraph.setValue(null, {emitEvent: false });
        this.startDateGraph.setValue(this.firstDay, { emitEvent: false });
        this.endDateGraph.setValue(this.secondDay, { emitEvent: false });
        this.getBranches();
      } else {
        this.storeGraph.setValue(store, {emitEvent: false });
        this.startDateGraph.setValue(this.firstDay, { emitEvent: false });
        this.endDateGraph.setValue(this.secondDay);
      }
    }
  }


  /**
    * selectCustomDate
    * @function
    * @description [get Graph in custom date]
    */
  selectCustomDate(event: {begin: string, end: string}) {
    let startDate: string, endDate: string;
      if (event.begin && event.end) {
        if (event.begin < event.end) {
          startDate = event.begin;
          endDate = event.end;
        } else {
          startDate = event.end;
          endDate = event.begin;
        }
      } else {
        startDate = event.begin;
        endDate = event.end;
      }
      this.firstDay = +this.formatDate(startDate, null, false, true);
      this.secondDay = +this.formatDate(endDate, null, false, true);
      if (event.begin === event.end) {
        this.getSelectedDates(this.formatDate(event.begin, '.', true, false));
      } else {
        this.getSelectedDates(this.formatDate(event.begin, '.', true, false) + ' - ' + this.formatDate(event.end, '.', true, false));
      }
  }

  /**
   * getBranches
   * @function
   * @description get stores
   */
  getBranches() {
    let clientId = null;
    if (this.hasChildren && this.clientChildrenGraph.value && this.clientChildrenGraph.value !== 'null') {
      clientId = this.clientChildrenGraph.value;
    }
    const branchSub = this.apiSrv.shopboxApiService.branchService.getBranches(null, null, clientId);
    this.apiSrv.apiType = API_TYPES.GET_BRANCHES;
    this.apiSrv.errorType = ERROR_TYPES.NORMAL;
    this.apiSrv.apiSubject.next(branchSub);
  }

  getChildrenClients() {
    const id = this.apiSrv.shopboxApiService.clientService.getId();
    const clientChildrenSub = this.apiSrv.shopboxApiService.clientService.getChildrenClients(id);
    this.apiSrv.apiType = API_TYPES.GET_CHILDREN_CLIENTS;
    this.apiSrv.errorType = ERROR_TYPES.NORMAL;
    this.apiSrv.apiSubject.next(clientChildrenSub);
  }

  /**
   * getData
   * @function
   * @description get Data of the whole dashboard
   */
   getData() {
    const model = this.graphGroup.getRawValue();

    if (model['start'] && model['end']) {

      if(!this.isAllClients) {
        this.currentStore = model['store'];
      } else {
        this.currentStore = 'null';
      }

      this.currentChildrenClientId = this.hasChildren ? model['child'] : null;
      this.dateStart = model['start'];
      this.dateEnd   = model['end'];
      const graphNewMode = model['type'] == null ? GraphDataFilters.Today : model['type'];

      // const topStaffSub = this.apiSrv.shopboxApiService.graphService.getTopStaff(
      //   this.currentStore,
      //   this.dateStart,
      //   this.dateEnd,
      //   this.graphMode,
      //   this.hasChildren ? this.currentChildrenClientId : null
      //   ).pipe(take(1),catchError((error:any) => {
      //   this.dialogSrv.toast("Top Staff Data Failed, Please try again", this.viewRef, 3200);
      //   return of([]);
      // }));

      // const topItemsSub = this.apiSrv.shopboxApiService.graphService.getTopItem(
      //   this.currentStore,
      //   this.dateStart,
      //   this.dateEnd,
      //   this.graphMode,
      //   this.hasChildren ? this.currentChildrenClientId : null
      //   ).pipe(take(1),catchError((error:any) => {
      //     this.dialogSrv.toast("Top Items Data Failed, Please try again", this.viewRef, 3200);
      //     return of([]);
      //   }));

      // const topTagSub = this.apiSrv.shopboxApiService.graphService.getTopTag(
      //   this.currentStore,
      //   this.dateStart,
      //   this.dateEnd,
      //   this.graphMode,
      //   this.hasChildren ? this.currentChildrenClientId : null
      //   ).pipe(take(1),catchError((error:any) => {
      //     this.dialogSrv.toast("Top Categories Data Failed, Please try again", this.viewRef, 3200);
      //     return of([]);
      //   }));

      const topSections = this.apiSrv.shopboxApiService.graphService.getTopSection(
        this.currentStore,
        graphNewMode,
        this.dateStart,
        this.dateEnd,
        this.graphMode,
        this.hasChildren
        ).pipe(take(1),catchError((error:any) => {
          this.dialogSrv.toast("Top Categories Data Failed, Please try again", this.viewRef, 3200);
          return of([]);
        }));
        if(this.clientChildrenGraph.value != null) {
          this.hasChildren = false
        }
      const graphSub = this.apiSrv.shopboxApiService.graphService.getNewGraph(
        this.currentStore,
        this.dateStart,
        this.dateEnd,
        graphNewMode,
        this.hasChildren,
        this.currentChildrenClientId
        ).pipe(take(1),catchError((error:any) => {
          this.dialogSrv.toast("Dashboard News Data Failed, Please try again", this.viewRef, 3200);
          return of([]);
        }));
      // const sources = [graphSub, topStaffSub, topItemsSub, topTagSub, topSections];
      const sources = [graphSub, topSections];

      if(this.clientChildrenGraph.value != null) {
        this.hasChildren = true
      }

      this.apiSrv.apiType = API_TYPES.GET_GRAPH;
      this.apiSrv.errorType = ERROR_TYPES.NORMAL;
      this.apiSrv.apiSubject.next(combineLatest(sources));
    }
  }


  setGraphLabels() {
    let graphLabels: any[] = [];
    // MONTH
    if (this.graphMode == 0) {
      this.dashboardDataView.graphDate.forEach(element => {
        graphLabels.push(Utilities.months[+element - 1]);
      });
      // WEEK
    } else if (this.graphMode == 1) {
      this.dashboardDataView.graphDate.forEach(element => {
        graphLabels.push('Week ' + element);
      });
    } else {
      graphLabels = this.dashboardDataView.graphDate;
    }
    this.dataGraph.labels = graphLabels;
    this.changeProgress(false, this._changeDetectRef);
  }

  getLastDateOfMonth(data: number): number {
    const day: Date = new Date(data * 1000);
    const lastDayOfMonth = new Date(day.getFullYear(), day.getMonth() + 1, 0);
    const result: number = lastDayOfMonth.getDate();
    return result;
  }

  getMonthFromDate(timeStamp: any): string {
    let result: string;
    if (typeof timeStamp === 'number') {
      const d = new Date(timeStamp * 1000);
      const locale = 'en-us';
      const month = d.toLocaleString(locale, { month: 'short' });
      result = month;
    }
    return result;
  }

  setChartDate() {
    this.currentDate = '0';
  }

  updateTheChartView() {
    this.chartView = true;
    if (this.pChart && this.pChart.chart) {
      // Change the display size
      this.pChart.width = this.widthSV;
      this.pChart.height = this.heightSV;
      this.pChart.reinit();
    }
    this._changeDetectRef.markForCheck();
  }

  resetCaches() {
    this.chartView = true;
    this.cacheSrv.deleteAllCachesContain(Graph.URL);
    this.cacheSrv.deleteAllCachesContain(Graph.URLTopItem);
    this.cacheSrv.deleteAllCachesContain(Graph.URLTopStaff);
    this.cacheSrv.deleteAllCachesContain(Graph.URLTopTag);
  }

  resize() {
    this.setChartValues();
  }

  trackItemFn(index: number, _item: any) {
    return index;
  }

  setVisibleCompareValues(value: boolean) {
    this.isCompareRevenueOn = value;
  }

  isSign(num: number | string): boolean | null {
    if (!num || num === 0 || num === '0' || num === '0.00') return null;
    return Math.sign(+num) === -1 ? true : false;
  }

  changeVatValues(val: boolean): void {
    this.vatOnValue = val;
    if (val) {
      this.dashboardDataView = this.dashboardData;
      this.dataGraph.datasets[0].data = this.dashboardData.graphData;
    } else {
      this.dashboardDataView = this.dashboardDataNotVat;
      this.dataGraph.datasets[0].data = this.dashboardDataNotVat.graphData;
    }
    this.setGraphLabels();
    if (this.dashboardDataView.comparedStart && this.dashboardDataView.comparedEnd) {
      this.compareRevenueToDates = this.dashboardDataView.comparedStart.replace(/\-/g, '.') + ' - ' + this.dashboardData.comparedEnd.replace(/\-/g, '.');
    }
    this.topTags = this.getTopsOnVat(this.topTags, this.vatOnValue);
    this.topStaff = this.getTopsOnVat(this.topStaff, this.vatOnValue);
    this.topItems = this.getTopsOnVat(this.topItems, this.vatOnValue);
    this.updateTheChartView();
  }

  getTopsOnVat(collection: any, vat: boolean): any {
    collection.forEach(element => {
      element.viewValue = new Utilities().formatMoney(String(vat ? element.total: element.total_excl_vat), this.currentCurrency);
    });
    return collection;
  }

  changeChartType(graphTypes: GraphDataMode): void {
    switch (graphTypes) {
      case GraphDataMode.Daily:
        this.graphMode = 2;
        this.graphLabel = 'BY_DAY';
        break;

      case GraphDataMode.Monthly:
        this.graphMode = 0;
        this.graphLabel = 'BY_MONTH';
        break;

      case GraphDataMode.Weekly:
        this.graphMode = 1;
        this.graphLabel = 'BY_WEEK';
        break;

      default:
        this.graphMode = 3;
        this.graphLabel = 'BY_HOUR';
        break;
    }
  }

  ngOnDestroy() {
    this.apiSrv.destroy();
  }

}
